import React from "react";
import {flexRender} from "@tanstack/react-table";
import List from "../common/table/list";
import {classNames as cn} from "../../utils/Utils";

const isActionColumn = (column) => {
    if (!column) {
        return false;
    }
    if (column.id === "actions") {
        return true;
    }
    const meta = column.columnDef?.meta ?? {};
    return meta.isActionColumn === true;
};

const isHiddenOnMobile = (column) => {
    if (!column) {
        return false;
    }
    const meta = column.columnDef?.meta ?? {};
    return meta.hideOnMobile === true || meta.isMobileHidden === true;
};

const getPrimaryCell = (cells) => {
    if (!Array.isArray(cells) || cells.length === 0) {
        return null;
    }
    const explicitlyMarked = cells.find((cell) => cell?.column?.columnDef?.meta?.mobilePrimary === true);
    if (explicitlyMarked) {
        return explicitlyMarked;
    }
    return cells[0] ?? null;
};

const getMobileCells = (row) => {
    if (!row) {
        return {
            primaryCell: null,
            detailCells: [],
            actionCells: [],
        };
    }

    const visibleCells = row.getVisibleCells().filter((cell) => !isHiddenOnMobile(cell.column));

    const actionCells = visibleCells.filter((cell) => isActionColumn(cell.column));
    const infoCells = visibleCells.filter((cell) => !isActionColumn(cell.column));

    const primaryCell = getPrimaryCell(infoCells);
    const detailCells = primaryCell ? infoCells.filter((cell) => cell !== primaryCell) : infoCells;

    return {
        primaryCell,
        detailCells,
        actionCells,
    };
};

const DataTableMobile = ({
    table,
    isLoading,
    resolveColumnLabel,
    onClickRow,
}) => {
    const rows = table?.getRowModel()?.rows ?? [];
    const hasRows = rows.length > 0;
    const isInteractive = typeof onClickRow === "function";

    return (
        <div className="sm:hidden">
            <List isLoading={isLoading} dataLength={rows.length}>
                {hasRows && rows.map((row) => {
                    const {primaryCell, detailCells, actionCells} = getMobileCells(row);
                    const handleRowClick = () => {
                        if (!isInteractive) {
                            return;
                        }
                        const identifier = row?.original?.id ?? row?.id;
                        if (identifier !== undefined) {
                            onClickRow(identifier);
                        }
                    };

                    const handleKeyDown = (event) => {
                        if (!isInteractive) {
                            return;
                        }
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleRowClick();
                        }
                    };

                    return (
                        <li key={row.id} className="p-4">
                            <div className="flex items-start gap-3">
                                <div
                                    className={cn(
                                        "flex-1 min-w-0",
                                        isInteractive && "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                    )}
                                    role={isInteractive ? "button" : undefined}
                                    tabIndex={isInteractive ? 0 : undefined}
                                    onClick={isInteractive ? handleRowClick : undefined}
                                    onKeyDown={isInteractive ? handleKeyDown : undefined}
                                >
                                    <div className="flex flex-col gap-2">
                                        {primaryCell && flexRender(
                                            primaryCell.column.columnDef.cell,
                                            primaryCell.getContext()
                                        )}
                                    </div>
                                </div>
                                {actionCells.length > 0 && (
                                    <div
                                        className="flex flex-col items-end gap-2"
                                        onClick={(event) => event.stopPropagation()}
                                        onKeyDown={(event) => event.stopPropagation()}
                                    >
                                        {actionCells.map((cell) => (
                                            <div key={cell.id} className="flex items-center justify-end">
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {detailCells.length > 0 && (
                                <div className="mt-4 space-y-3">
                                    {detailCells.map((cell) => {
                                        const label = typeof resolveColumnLabel === "function"
                                            ? resolveColumnLabel(cell.column)
                                            : cell.column.id;

                                        return (
                                            <div
                                                key={cell.id}
                                                className="flex items-start justify-between gap-3 text-sm"
                                            >
                                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    {label}
                                                </span>
                                                <div className="flex min-w-[40%] max-w-[60%] flex-col items-end gap-1 text-right text-gray-900">
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </li>
                    );
                })}
            </List>
        </div>
    );
};

export default DataTableMobile;
